import { ReactNode, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmbedUrlRequest } from 'vopay';
import ELinxConnect, { EventMessage } from './ELinxConnect';
import { ELinxConnectContext, OpenELinxConnect } from './useELinxConnect';

// Mounted once (composed into SharedProviders, so it has Api/Toast). Owns the single eLinx overlay and
// builds the embed request from the raw inputs open() is given, so consumers render nothing.
export default function ELinxConnectProvider({ children }: { children: ReactNode }) {
  const [setting, setSetting] = useState<EmbedUrlRequest | null>(null);
  const resolver = useRef<{ resolve: (token: string) => void; reject: (error: Error) => void } | null>(null);

  const onSuccess = (event: EventMessage) => {
    setSetting(null);
    resolver.current?.resolve(event.data.Token);
    resolver.current = null;
  };

  const onClose = () => {
    setSetting(null);
    resolver.current?.reject(new Error('User cancelled ELinxConnect'));
    resolver.current = null;
  };

  // Built once so consumers get a stable service identity (see AlertDialog.provider / Dialog.provider).
  const service = useMemo<{ open: OpenELinxConnect }>(
    () => ({
      open: (values, country, clientAccountsEnabled, accountID) =>
        new Promise((resolve, reject) => {
          setSetting({
            Country: country,
            RedirectURL: 'https://vopay.com',
            RedirectMethod: 'JavascriptMessage',
            ClientAccountID: clientAccountsEnabled ? accountID : '',
            ContactID: !clientAccountsEnabled ? accountID : '',
            ...values,
          });

          resolver.current = { resolve, reject };
        }),
    }),
    [],
  );

  return (
    <ELinxConnectContext.Provider value={service}>
      {children}

      {setting &&
        createPortal(<ELinxConnect setting={setting} onSuccess={onSuccess} onClose={onClose} />, document.body)}
    </ELinxConnectContext.Provider>
  );
}
